/**
 * Sync protocol between a terminal and the server.
 *
 * Two directions with deliberately different authority:
 *
 *   push — client-authoritative. A completed sale is a fact that already
 *          happened in the physical world. The server records it. It does not
 *          re-price it against the current catalog and it does not reject it
 *          for being stale. It rejects only what it cannot parse or attribute.
 *
 *   pull — server-authoritative. Catalog rows are replaced wholesale from the
 *          server's version. The terminal never edits catalog data, so there
 *          is no merge to get wrong.
 *
 * Every envelope carries a client-generated UUID that is also the primary key
 * of the row it creates. Re-sending is therefore a no-op, which is what makes
 * blind retry over a flaky mobile connection safe.
 */
import { z } from 'zod';
import { AppConfig, Category, DiscountReason, PaymentTerminal, Product, Sale, Shift, StaffPublic, isoDateTime, nonNegativeCents, uuid, } from './domain.js';
export const SYNC_PROTOCOL_VERSION = 1;
// ---------------------------------------------------------------------------
// Push: outbox envelopes
// ---------------------------------------------------------------------------
const envelopeBase = {
    /** Also the primary key of the row this envelope creates. */
    id: uuid,
    /** Device clock. Reports use this. */
    occurredAt: isoDateTime,
    terminalId: uuid,
    staffId: uuid,
};
export const ShiftOpenEnvelope = z.object({
    ...envelopeBase,
    kind: z.literal('shift_open'),
    openingBalanceCents: nonNegativeCents,
});
export const ShiftCloseEnvelope = z.object({
    ...envelopeBase,
    kind: z.literal('shift_close'),
    shiftId: uuid,
    closingBalanceCents: nonNegativeCents,
});
export const SaleEnvelope = z.object({
    ...envelopeBase,
    kind: z.literal('sale'),
    sale: Sale,
});
export const SaleVoidEnvelope = z.object({
    ...envelopeBase,
    kind: z.literal('sale_void'),
    saleId: uuid,
    reason: z.string().trim().min(1),
});
export const TicketPrintEnvelope = z.object({
    ...envelopeBase,
    kind: z.literal('ticket_print'),
    saleId: uuid,
    isReprint: z.boolean(),
    copyNo: z.number().int().positive(),
});
export const OutboxEnvelope = z.discriminatedUnion('kind', [
    ShiftOpenEnvelope,
    ShiftCloseEnvelope,
    SaleEnvelope,
    SaleVoidEnvelope,
    TicketPrintEnvelope,
]);
// ---------------------------------------------------------------------------
// Request and response
// ---------------------------------------------------------------------------
/**
 * Optional device telemetry (2026-07-31, self-update / deployed-base
 * visibility). Every field is optional and the whole block is optional on
 * `SyncRequest` — a terminal running last month's build that has never heard
 * of this shape must keep validating exactly as it did before. Stored
 * verbatim as `terminals.device_info` jsonb (db/migrations/006) so a new
 * field showing up here later is not itself a migration.
 */
export const SyncDevice = z.object({
    /** Hardware model, e.g. "Sunmi D3 mini". */
    model: z.string().optional(),
    /** OS family, e.g. "android". */
    platform: z.string().optional(),
    osVersion: z.string().optional(),
    /** The native build's own versionCode — may differ from the outer
     * `appVersion` string field below, which is unstructured and predates this. */
    appVersionCode: z.number().int().optional(),
    appVersionName: z.string().optional(),
    /**
     * The device's IANA time zone as the OS reports it (Android derives it
     * from network/location when automatic time zone is on). The server
     * treats this as the terminal's REPORTED zone; an admin override on the
     * terminal record wins over it. See SyncResponse.terminal.
     */
    timeZone: z.string().optional(),
});
export const SyncRequest = z.object({
    protocolVersion: z.literal(SYNC_PROTOCOL_VERSION),
    terminalId: uuid,
    appVersion: z.string(),
    /** Opaque catalog cursor from the previous response. Null on first sync. */
    cursor: z.string().nullable(),
    /** Device clock at request time, so the server can measure drift. */
    clientTime: isoDateTime,
    outbox: z.array(OutboxEnvelope).max(500),
    /** See `SyncDevice`. Absent for any client older than this feature. */
    device: SyncDevice.optional(),
    /** Structured device logs collected since the last sync. Best effort. */
    diagnostics: z
        .array(z.object({
        at: isoDateTime,
        level: z.enum(['trace', 'debug', 'info', 'warn', 'error']),
        message: z.string(),
        attributes: z.record(z.unknown()).optional(),
    }))
        .max(500)
        .optional(),
});
export const SyncRejectionCode = z.enum([
    'malformed',
    'unknown_staff',
    'unknown_shift',
    'unknown_sale',
    'shift_already_closed',
    'shift_not_open',
    'totals_mismatch',
    'discount_without_reason',
    'terminal_inactive',
    'protocol_version',
    'internal',
]);
export const SyncResponse = z.object({
    protocolVersion: z.literal(SYNC_PROTOCOL_VERSION),
    /** Envelope ids the server has durably stored. Safe to drop from the outbox. */
    applied: z.array(uuid),
    /**
     * Envelopes the server refused. These stay on the device in a dead state and
     * raise an admin-visible flag. They are never silently discarded.
     */
    rejected: z.array(z.object({
        id: uuid,
        code: SyncRejectionCode,
        message: z.string(),
    })),
    catalog: z.object({
        categories: z.array(Category),
        products: z.array(Product),
        staff: z.array(StaffPublic),
        discountReasons: z.array(DiscountReason),
        // Optional so an older server's response (one still on the migration
        // before 012_payment_terminals.sql) still parses — same house rule as
        // DiscountReason.defaultPct above. Without this, a terminal running
        // this shared package against pos01 TODAY throws in SyncResponse.parse
        // on every sync: the outbox never drains and the catalog never
        // refreshes. Found in PR review.
        paymentTerminals: z.array(PaymentTerminal).default([]),
        config: AppConfig,
        /** Tombstones, so a terminal that was offline learns about deletions. */
        deleted: z.object({
            categories: z.array(uuid),
            products: z.array(uuid),
            staff: z.array(uuid),
            discountReasons: z.array(uuid),
            paymentTerminals: z.array(uuid).default([]),
        }),
    }),
    /** Feed back into the next request. */
    cursor: z.string(),
    serverTime: isoDateTime,
    /** Open shift the server believes this terminal has, if any. */
    openShift: Shift.nullable(),
    /**
     * Server-authoritative facts about THIS terminal. `timeZone` is the
     * effective zone the terminal must use for its local report day
     * boundaries: the admin's override if one is set, else the zone the
     * device itself reported, else the business default. Optional so an
     * older server's response still parses.
     */
    terminal: z
        .object({
        timeZone: z.string(),
        /** The terminal's name as the ADMIN has it. Once paired, the server
         * is authoritative: an admin rename reaches the device on its next
         * sync (the gap that left the device printing "Caja principal" while
         * the server said "Caja 1", 2026-08-01). Optional so a response from
         * a server predating it still parses. */
        name: z.string().optional(),
    })
        .optional(),
});
/** Local outbox record states. `dead` rows require human attention. */
export const OutboxState = z.enum(['pending', 'in_flight', 'dead']);
export const MAX_CLOCK_DRIFT_MS = 5 * 60 * 1000;
export const OUTBOX_BATCH_SIZE = 100;
//# sourceMappingURL=sync.js.map